// Grammar Units 1 Data - Unit 1: English Five Sentence Patterns
// Based on Ch01 英文五大句型.jsx
// Organized for Supabase migration and systematic data management

export const grammarUnits1Data = [
  {
    id: 1,
    title: { traditional: '英文五大句型', simplified: '英文五大句型', english: 'English Five Sentence Patterns' },
    subtitle: { traditional: '掌握英文句型的核心結構', simplified: '掌握英文句型的核心结构', english: 'Master the core structure of English sentence patterns' },
    description: { traditional: '系統性學習英文五大句型，包括完全不及物動詞、不完全不及物動詞、完全及物動詞、授與動詞、不完全及物動詞。', simplified: '系统性学习英文五大句型，包括完全不及物动词、不完全不及物动词、完全及物动词、授与动词、不完全及物动词。', english: 'Systematic learning of English five sentence patterns including intransitive verbs, linking verbs, transitive verbs, dative verbs, and complex transitive verbs.' },
    icon: '📝',
    color: 'from-blue-500 to-cyan-600',
    status: 'notStarted',
    difficulty: 'beginner',
    estimatedHours: 4,
    content: {
      overview: {
        title: "英文五大句型概覽",
        description: "英文句子可以分為五大基本句型，每種句型都有其特定的結構和用法。",
        patterns: [
          {
            id: 1,
            name: "完全不及物動詞",
            structure: "S. + Vi.",
            description: "主詞 + 不及物動詞",
            chinese: "她笑了",
            example: "She smiled.",
            explanation: "不及物動詞本身可表達完整的概念，不需要接受詞",
            components: {
              S: "主詞 - 指「你、我、他、你們、我們、他們、它、它們」",
              Vi: "不及物動詞 - 表示 intransitive，指「不及物」，亦指動詞本身可表達完整的語意，不需要接補語"
            },
            examples: [
              { en: "The children play.", zh: "孩子們在玩耍" },
              { en: "She smiled.", zh: "她笑了" },
              { en: "The firecrackers went off.", zh: "爆竹爆炸了" }
            ],
            phrasal_verbs: ["break down", "calm down", "give up", "dress up", "get up", "go off", "look out", "set out"]
          },
          {
            id: 2,
            name: "不完全不及物動詞",
            structure: "S. + Vi. + S.C.",
            description: "主詞 + 不及物動詞 + 主詞補語",
            chinese: "他是好老師",
            example: "He is a good teacher.",
            explanation: "用來連接主詞與主詞補語的不及物動詞作連繫動詞",
            components: {
              S: "主詞",
              Vi: "連繫動詞",
              SC: "主詞補語 - 補語是補充說明，主詞補語就是補充說明主詞的狀態，通常是名詞、形容詞"
            },
            linking_verbs: {
              be: ["be動詞", "是"],
              sensory: ["look", "taste", "sound", "feel", "smell"],
              change: ["become", "turn", "get", "go", "grow", "fall"],
              state: ["keep", "remain", "stay", "prove", "stand", "seem", "appear"]
            },
            examples: [
              { en: "He is a good teacher.", zh: "他是好老師" },
              { en: "Your sister is very cute.", zh: "你妹妹很可愛" },
              { en: "The milk smells funny.", zh: "冰箱裡的牛奶聞起來怪怪的" }
            ]
          },
          {
            id: 3,
            name: "完全及物動詞",
            structure: "S. + Vt. + O.",
            description: "主詞 + 及物動詞 + 受詞",
            chinese: "我很愛你",
            example: "I love you so much.",
            explanation: "及物動詞之後需要接受詞句意才完整，這類的動詞又稱為「完全及物動詞」",
            components: {
              S: "主詞",
              Vt: "及物動詞 - 表示 transitive，指「及物」，亦指此類動詞之後需按受詞，才能表達完整的語意",
              O: "受詞 - 可以是東西或是人，通常為名詞、動名詞、不定詞等"
            },
            examples: [
              { en: "I love you so much.", zh: "我很愛你" },
              { en: "Toby ate everything on his plate.", zh: "托比把盤子裡的東西全都吃光了" },
              { en: "Vicky never remembers to turn off the lights.", zh: "薇琪從不記得要關燈" }
            ],
            phrasal_verbs: [
              { verb: "take advantage of", meaning: "利用" },
              { verb: "take care of", meaning: "照顧" },
              { verb: "make use of", meaning: "利用" },
              { verb: "take a look at", meaning: "看一看" }
            ]
          },
          {
            id: 4,
            name: "授與動詞",
            structure: "S. + Vt. + I.O. + D.O.",
            description: "主詞 + 及物動詞 + 間接受詞 + 直接受詞",
            chinese: "他給我看他的新皮夾",
            example: "He shows me his new wallet.",
            explanation: "主詞 + 授與動詞 + 間接受詞（人）+ 直接受詞（物）",
            components: {
              S: "主詞",
              Vt: "及物動詞",
              IO: "間接受詞 - 間接受詞通常是人",
              DO: "直接受詞 - 直接受詞通常是事物"
            },
            dative_verbs: {
              with_to: ["bring", "give", "hand", "mail", "pass", "read", "sell", "send", "show", "take", "write"],
              with_for: ["buy", "cook", "find", "get", "leave", "make", "build", "order", "fetch", "play", "sing"],
              with_of: ["ask", "beg", "inquire", "rob"],
              with_from: ["borrow"]
            },
            special_verbs: ["cost", "envy", "wish"],
            examples: [
              { en: "He shows me his new wallet.", zh: "他給我看他的新皮夾" },
              { en: "Mr. Gibbons sent you two tickets for the show.", zh: "吉朋斯先生寄了兩張表演的票給你" },
              { en: "Mrs. Hansen bought me a drink.", zh: "韓森太太買了一杯飲料給我" }
            ]
          },
          {
            id: 5,
            name: "不完全及物動詞",
            structure: "S. + Vt. + O. + O.C.",
            description: "主詞 + 及物動詞 + 受詞 + 受詞補語",
            chinese: "這些禮物讓我很開心",
            example: "The gifts make me very happy.",
            explanation: "受詞補語就是補充說明受詞的狀態或性質",
            components: {
              S: "主詞",
              Vt: "及物動詞",
              O: "受詞",
              OC: "受詞補語 - 可用名詞、形容詞、現在分詞、過去分詞、原形動詞、不定詞、地方副詞片語等作補語"
            },
            verb_types: {
              appointment: {
                verbs: ["appoint", "elect", "name", "label"],
                pattern: "+ O. + 名詞",
                note: "這類動詞之後的受詞補語大多為名詞，不過 call 之後的受詞補語也可為形容詞"
              },
              causative: {
                verbs: ["keep", "leave", "make", "get"],
                pattern: "+ O. + 形容詞/分詞(現在分詞或過去分詞)/名詞",
                note: "這類動詞的受詞補語為形容詞、分詞(現在分詞或過去分詞)和名詞"
              },
              perception: {
                verbs: ["believe", "consider", "find", "think"],
                pattern: "+ O. + 形容詞/分詞/名詞",
                note: "這類動詞的受詞補語為形容詞、分詞和名詞"
              },
              see_view: {
                verbs: ["see", "view", "look on", "look upon", "think of", "refer to", "regard"],
                pattern: "+ O. + as + 受詞補語",
                note: "這類動詞之後的受詞補語前須先加上介系詞 as"
              }
            },
            examples: [
              { en: "The gifts make me very happy.", zh: "這些禮物讓我很開心" },
              { en: "The group elected him spokesman.", zh: "該團體選他為發言人" },
              { en: "She called me Honey.", zh: "她叫我甜心" },
              { en: "The old man looked upon Horatio as his son.", zh: "這個老人把何瑞修當作自己的兒子" }
            ]
          }
        ]
      },
      memory_tips: {
        title: "記憶口訣與技巧",
        tips: [
          "第一型：主詞單獨行動 (She smiled)",
          "第二型：主詞需要說明 (He is teacher)",
          "第三型：動作有對象 (I love you)",
          "第四型：給誰什麼東西 (He shows me wallet)",
          "第五型：讓受詞變怎樣 (Gifts make me happy)"
        ],
        learning_steps: [
          "步驟1：先找出主詞 (誰在做動作)",
          "步驟2：判斷動詞類型 (及物或不及物)",
          "步驟3：看後面跟什麼 (受詞、補語)",
          "步驟4：套用句型公式確認"
        ],
        common_mistakes: [
          "不要混淆及物動詞和不及物動詞的用法",
          "注意區分主詞補語和受詞補語",
          "授與動詞要記住哪些搭配 to，哪些搭配 for",
          "連繫動詞後面接的是補語，不是受詞",
          "多練習分析句子結構，培養語感"
        ]
      },
      quizzes: [
        {
          sentence: "She smiled.",
          options: ["S. + Vi.", "S. + Vi. + S.C.", "S. + Vt. + O.", "S. + Vt. + I.O. + D.O."],
          correct: 0,
          explanation: "She是主詞，smiled是不及物動詞，不需要受詞就能表達完整意思"
        },
        {
          sentence: "He is a good teacher.",
          options: ["S. + Vi.", "S. + Vi. + S.C.", "S. + Vt. + O.", "S. + Vt. + I.O. + D.O."],
          correct: 1,
          explanation: "He是主詞，is是連繫動詞，a good teacher是主詞補語"
        },
        {
          sentence: "I love you so much.",
          options: ["S. + Vi.", "S. + Vi. + S.C.", "S. + Vt. + O.", "S. + Vt. + I.O. + D.O."],
          correct: 2,
          explanation: "I是主詞，love是及物動詞，you是受詞"
        },
        {
          sentence: "He shows me his new wallet.",
          options: ["S. + Vt. + O.", "S. + Vt. + I.O. + D.O.", "S. + Vt. + O. + O.C.", "S. + Vi. + S.C."],
          correct: 1,
          explanation: "He是主詞，shows是授與動詞，me是間接受詞(人)，his new wallet是直接受詞(物)"
        },
        {
          sentence: "The gifts make me very happy.",
          options: ["S. + Vt. + O.", "S. + Vt. + I.O. + D.O.", "S. + Vt. + O. + O.C.", "S. + Vi. + S.C."],
          correct: 2,
          explanation: "The gifts是主詞，make是不完全及物動詞，me是受詞，very happy是受詞補語"
        }
      ]
    }
  }
];

// Export individual units for specific access
export const getUnitById = (unitId) => {
  return grammarUnits1Data.find(unit => unit.id === unitId);
};

// Export units by difficulty
export const getUnitsByDifficulty = (difficulty) => {
  return grammarUnits1Data.filter(unit => unit.difficulty === difficulty);
};

// Export completed units
export const getCompletedUnits = () => {
  return grammarUnits1Data.filter(unit => unit.status === 'completed');
};

// Default export
export default grammarUnits1Data;
